import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, Heart, ImagePlus, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MessageWall() {
  const [messages, setMessages] = useState([]);
  const [author, setAuthor] = useState("");
  const [text, setText] = useState("");
  const [photo, setPhoto] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const load = () => axios.get(`${API}/messages`).then((r) => setMessages(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) return toast.error("Photo must be under 4MB");
    const reader = new FileReader();
    reader.onload = () => setPhoto(reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!author.trim()) return toast.error("Please add your name");
    if (!text.trim() && !photo) return toast.error("Write a message or add a photo");
    setSubmitting(true);
    try {
      await axios.post(`${API}/messages`, { author, text, photo });
      toast.success("Posted to Chase's wall! 💜");
      setAuthor("");
      setText("");
      setPhoto("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="wall" className="py-24 md:py-32 bg-zinc-900/50 border-y border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <p className="font-sub text-xs tracking-[0.2em] uppercase font-bold text-purple-400 mb-3">Photos & Encouragement</p>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight uppercase mb-4">
          The <span className="text-purple-400">Wall</span>
        </h2>
        <p className="font-sub text-base md:text-lg text-zinc-400 max-w-2xl mb-12">
          Share a photo, a memory, or words of encouragement. Chase will see every single one.
        </p>

        <div className="rounded-3xl bg-zinc-950 border border-white/5 p-6 md:p-8 mb-14 max-w-3xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <Input data-testid="wall-author-input" placeholder="Your name" value={author} onChange={(e) => setAuthor(e.target.value)} className="bg-zinc-900 border-white/10" />
            <button
              data-testid="wall-photo-btn"
              onClick={() => fileRef.current?.click()}
              className="flex items-center justify-center gap-2 font-sub text-sm font-semibold border border-white/10 hover:border-purple-400 text-zinc-300 hover:text-white rounded-md h-9 px-4 transition-colors duration-200"
            >
              <ImagePlus className="w-4 h-4" /> {photo ? "Change Photo" : "Add a Photo"}
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="wall-photo-input" />
          </div>
          {photo && (
            <div className="relative inline-block mb-4">
              <img src={photo} alt="preview" className="h-32 rounded-xl border border-white/10 object-cover" />
              <button
                data-testid="wall-photo-remove"
                onClick={() => { setPhoto(""); if (fileRef.current) fileRef.current.value = ""; }}
                className="absolute -top-2 -right-2 bg-zinc-800 border border-white/20 rounded-full p-1 hover:bg-red-600 transition-colors duration-200"
                aria-label="Remove photo"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          <Textarea data-testid="wall-message-input" placeholder="Words of encouragement for Chase..." rows={3} value={text} onChange={(e) => setText(e.target.value)} className="bg-zinc-900 border-white/10 mb-4" />
          <Button data-testid="wall-submit-btn" onClick={submit} disabled={submitting} className="bg-purple-600 hover:bg-purple-500 font-sub font-semibold rounded-full px-8">
            <Heart className="w-4 h-4 mr-2" /> {submitting ? "Posting..." : "Post to the Wall"}
          </Button>
        </div>

        {messages.length === 0 ? (
          <p className="text-zinc-500 font-sub" data-testid="wall-empty-state">Be the first to post on Chase's wall! 💜</p>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-6" data-testid="wall-messages">
            {messages.map((m) => (
              <div key={m.id} data-testid={`wall-message-${m.id}`} className="break-inside-avoid mb-6 rounded-2xl bg-zinc-950 border border-white/5 hover:border-purple-500/40 overflow-hidden transition-colors duration-300">
                {m.photo && <img src={m.photo} alt={`From ${m.author}`} className="w-full object-cover max-h-72" />}
                <div className="p-6">
                  {m.text && <p className="font-sub text-zinc-200 leading-relaxed mb-3">"{m.text}"</p>}
                  <div className="flex items-center gap-2">
                    <Camera className="w-3.5 h-3.5 text-purple-400" />
                    <span className="font-sub text-sm font-semibold text-purple-300">{m.author}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
